// Profit-focused AMEEX KPI card for the admin dashboard.
//
// Mirrors AMEEX's "Globale" view but keeps only the numbers that drive
// profit decisions:
//   • Revenue collected (cash in pocket from delivered parcels)
//   • Pipeline (COD still in transit — owed but not collected)
//   • Delivered / In transit / Returned / Refused breakdown
//   • Delivery rate (delivered ÷ terminal outcomes)
//   • Top 5 cities by revenue
//
// "Sync depuis AMEEX" triggers POST /ameex/reconcile (same call the 6h
// cron makes) so the admin can force-refresh before reading the numbers
// instead of waiting on the cron tick.

import { useState, type CSSProperties } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ameexApi } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { formatPrice } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { rateOrTooEarly, MIN_SAMPLE } from '@/lib/homeDashboard';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

const STATUS_LABELS: Record<string, { label: string; tone: string }> = {
  pending:      { label: 'En attente',     tone: '#9CA3AF' },
  in_progress:  { label: 'En cours',       tone: '#3B82F6' },
  distribution: { label: 'En distribution', tone: '#0EA5E9' },
  delivered:    { label: 'Livré',          tone: '#10B981' },
  postponed:    { label: 'Reporté',        tone: '#F59E0B' },
  returned:     { label: 'Retourné',       tone: '#EF4444' },
  refused:      { label: 'Refusé',         tone: '#DC2626' },
  cancelled:    { label: 'Annulé',         tone: '#6B7280' },
  refunded:     { label: 'Remboursé',      tone: '#8B5CF6' },
  unresolved:   { label: 'À vérifier',     tone: '#F97316' },
};

export function AmeexInsightsCard({ range }: { range?: { from?: string; to?: string } }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [debugLoading, setDebugLoading] = useState(false);

  async function runDebug() {
    setDebugLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const qs = new URLSearchParams({ debug: '1' });
      if (range?.from) qs.set('from', range.from);
      if (range?.to)   qs.set('to', range.to);
      const res = await fetch(`${API_BASE}/ameex/insights?${qs}`, {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      const j = await res.json();
      setDebugInfo({ status: res.status, body: j });
    } catch (e: any) {
      setDebugInfo({ error: String(e?.message ?? e) });
    } finally {
      setDebugLoading(false);
    }
  }

  // Real-time: 30s auto-refresh + refetch on tab focus. The endpoint hits
  // AMEEX directly each call, so the dashboard mirrors AMEEX.ma without a
  // manual sync.
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['ameex-insights', range?.from ?? null, range?.to ?? null],
    queryFn: () => ameexApi.insights(range),
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const reconcile = useMutation({
    mutationFn: () => ameexApi.reconcile(),
    onSuccess: (res) => {
      setSyncMsg(t('Synchronisé · {n} mis à jour', { n: res.updated ?? 0 }));
      qc.invalidateQueries({ queryKey: ['ameex-insights'] });
      setTimeout(() => setSyncMsg(null), 4000);
    },
    onError: (e: any) => {
      setSyncMsg(e?.message ?? t('Échec de la synchronisation'));
      setTimeout(() => setSyncMsg(null), 4000);
    },
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl p-5 border h-40 animate-pulse"
           style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }} />
    );
  }
  if (error || !data) {
    return (
      <div className="bg-white rounded-xl p-5 border" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
        <p className="text-sm text-red-600 font-medium mb-2">{t('Impossible de charger les KPIs AMEEX.')}</p>
        <pre className="text-[11px] bg-red-50 border border-red-200 rounded p-2 overflow-auto whitespace-pre-wrap break-all">
          {error instanceof Error ? error.message : JSON.stringify(error, null, 2)}
        </pre>
      </div>
    );
  }

  const { totals, revenue, delivery_rate, counts_by_status, top_cities, fetched_at } = data;
  // « 4 colis — trop tôt » under MIN_SAMPLE finished parcels (owner, 2026-10-21):
  // a rate on 4 parcels swings 25 points with one refusal.
  const finished = totals.delivered + totals.returned + totals.refused;
  const early = rateOrTooEarly(totals.delivered, finished).kind === 'tooEarly' && finished > 0;
  const rate = delivery_rate == null || early ? null : Math.round(delivery_rate * 1000) / 10;
  const fetchedAtLabel = fetched_at
    ? new Date(fetched_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  // Order status rows by canonical priority — delivered first since it's the
  // profit-driving outcome, then in-transit buckets, then losses.
  const STATUS_ORDER = [
    'delivered', 'in_progress', 'distribution', 'postponed',
    'returned', 'refused', 'cancelled', 'refunded', 'pending', 'unresolved',
  ];
  const statusRows = STATUS_ORDER
    .filter((k) => (counts_by_status[k] ?? 0) > 0)
    .map((k) => ({ key: k, count: counts_by_status[k] }));

  return (
    <div
      className="bg-white rounded-xl border overflow-hidden animate-[fadeInUp_0.5s_ease-out_both] mb-7"
      style={{
        borderColor: 'rgba(201, 162, 74, 0.15)',
        boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04), 0 8px 24px rgba(31, 41, 55, 0.04)',
        animationDelay: '300ms',
      }}
    >
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3"
           style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
        <div>
          <h3
            className="text-lg flex items-center gap-2 text-[#1F2937] dark:text-adm-ink"
            style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
          >
            📦 {t('Livraisons AMEEX')}
            <span
              className="inline-flex items-center gap-1 text-[10px] font-sans uppercase tracking-wide px-1.5 py-0.5 rounded"
              style={{ background: '#10B98115', color: '#10B981' }}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isFetching ? 'animate-pulse' : ''}`} style={{ background: '#10B981' }} />
              {t('Live')}
            </span>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('Direct depuis AMEEX · {n} colis', { n: totals.parcels })}
            {fetchedAtLabel && <span className="ml-2 text-gray-400">{t('· maj {time}', { time: fetchedAtLabel })}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {syncMsg && <span className="text-xs text-gray-500">{syncMsg}</span>}
          <button
            onClick={runDebug}
            disabled={debugLoading}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50"
            style={{
              borderColor: 'var(--color-adm-line)',
              background: 'var(--color-adm-surface)',
              color: 'var(--color-adm-muted)',
            }}
            title={t('Affiche la réponse brute d’AMEEX pour diagnostiquer pourquoi les KPIs sont à 0')}
          >
            {debugLoading ? '…' : `🔍 ${t('Diagnostic')}`}
          </button>
          <button
            onClick={() => reconcile.mutate()}
            disabled={reconcile.isPending}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 text-[#1F2937] dark:text-adm-ink"
            style={{ borderColor: 'rgba(201, 162, 74, 0.30)', background: reconcile.isPending ? 'var(--color-adm-surface-2)' : 'var(--color-adm-surface)' }}
            title={t('Force la mise à jour des statuts AMEEX dans notre base (en plus du flux temps réel)')}
          >
            {reconcile.isPending ? t('Synchronisation…') : `⟳ ${t('Sync DB')}`}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5">
        <KpiTile
          label={t('Encaissé (livrés)')}
          value={formatPrice(revenue.collected)}
          sub={t('{n} colis livrés', { n: totals.delivered })}
          color="#10B981"
        />
        <KpiTile
          label={t('En attente d’encaissement')}
          value={formatPrice(revenue.in_transit)}
          sub={t('{n} colis en route', { n: totals.in_transit })}
          color="#3B82F6"
        />
        <KpiTile
          label={t('Taux de livraison')}
          value={early ? t('{n} colis — trop tôt', { n: finished }) : rate == null ? '—' : `${rate}%`}
          sub={
            early
              ? t('Pas de taux sous {n} colis terminés', { n: MIN_SAMPLE })
              : rate == null
              ? t('Aucun colis terminé')
              : t('{delivered} sur {total} terminés', { delivered: totals.delivered, total: totals.delivered + totals.returned + totals.refused })
          }
          color={rate == null ? '#6B7280' : rate >= 80 ? '#10B981' : rate >= 60 ? '#F59E0B' : '#EF4444'}
        />
        <KpiTile
          label={t('Pertes (retours + refus)')}
          value={String(totals.returned + totals.refused)}
          sub={t('{returned} retour · {refused} refus', { returned: totals.returned, refused: totals.refused })}
          color="#EF4444"
        />
      </div>

      {statusRows.length > 0 && (
        <div className="px-5 pb-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {t('Répartition par statut')}
          </p>
          <div className="flex flex-wrap gap-2">
            {statusRows.map(({ key, count }) => {
              const meta = STATUS_LABELS[key] ?? { label: key, tone: '#6B7280' };
              return (
                <span
                  key={key}
                  // Text in the series hue on a light card; at night the hue
                  // stays on the dot, tint and edge and the words go to ink-2
                  // (several hues are < 4.5:1 as small text on a dark card).
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-[color:var(--chip-tone)] dark:text-adm-ink-2"
                  style={{
                    background: `${meta.tone}10`,
                    '--chip-tone': meta.tone,
                    border: `1px solid ${meta.tone}30`,
                  } as CSSProperties}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.tone }} />
                  {t(meta.label)}
                  <span className="font-semibold">{count}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {top_cities.length > 0 && (
        <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: 'rgba(201, 162, 74, 0.10)' }}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {t('Top villes (livraisons réussies)')}
          </p>
          <div className="space-y-2">
            {top_cities.map((c) => {
              const max = top_cities[0]?.count ?? 1;
              const pct = Math.max(4, Math.round((c.count / max) * 100));
              return (
                <div key={c.city_name} className="flex items-center gap-3">
                  <span className="text-xs text-gray-700 w-32 truncate" title={c.city_name}>{c.city_name}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${pct}%`, background: '#C9A24A' }}
                    />
                  </div>
                  <span className="text-xs font-semibold text-gray-800 w-10 text-right">{c.count}</span>
                  <span className="text-xs text-gray-500 w-20 text-right">{formatPrice(c.revenue)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {debugInfo && (
        <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: 'rgba(201, 162, 74, 0.10)' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {t('Diagnostic AMEEX')}
            </p>
            <button
              onClick={() => setDebugInfo(null)}
              className="text-xs text-gray-400 hover:text-gray-700"
            >
              ✕ {t('fermer')}
            </button>
          </div>
          <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-96 whitespace-pre-wrap break-all">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
  color,
}: { label: string; value: string; sub: string; color: string }) {
  return (
    <div
      className="rounded-lg p-3 border"
      style={{
        background: `${color}08`,
        borderColor: `${color}20`,
      }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
        {label}
      </p>
      <p className="text-xl font-bold" style={{ color }}>{value}</p>
      <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>
    </div>
  );
}
